/**
 * Admin FAQ List Page
 * 
 * Primary admin screen for managing FAQs.
 * Features:
 * - Table layout (desktop) / Card layout (mobile)
 * - Instant debounced search
 * - Filter by category and status
 * - Clear visual indicators for published/draft
 * - Confirmation dialogs for destructive actions
 * 
 * UX Decisions:
 * - Green badge for Published, Gray for Draft - industry standard status colors
 * - Provider icons indicate media presence without loading
 * - Delete requires confirmation to prevent accidental data loss
 * - Empty state with helpful CTA guides new users
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Skeleton,
  Card,
  CardContent,
  CardActions,
  Stack,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Visibility';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LoomIcon from '@mui/icons-material/VideoLibrary';
import DocsIcon from '@mui/icons-material/Description';
import SheetsIcon from '@mui/icons-material/TableChart';
import SlidesIcon from '@mui/icons-material/Slideshow';
import DriveIcon from '@mui/icons-material/Folder';
import CategoryIcon from '@mui/icons-material/Category';
import FilterIcon from '@mui/icons-material/FilterList';
import EmptyIcon from '@mui/icons-material/HelpOutline';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { debounce } from 'lodash';

import MainCard from '../../../components/MainCard';
import CustomButton from '../../../components/@extended/CustomButton';
import { CustomTableHead, CustomTableHeadCell, CustomTableRow } from '../../../components/@extended/CustomTable';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import TablePagination from '../../../components/common/TablePagination';
import { useGetFaqsQuery, useDeleteFaqMutation, useGetFaqCategoriesQuery } from '../../../store/slices/apiSlice';
import { useResponsive } from '../../../hooks/ResponsiveProvider';
import FaqImportExport from './FaqImportExport';

// Media provider icon mapping
const MediaProviderIcon = ({ provider }) => {
  switch (provider) {
    case 'YOUTUBE':
      return (
        <Tooltip title="YouTube video attached">
          <YouTubeIcon sx={{ color: '#FF0000', fontSize: 20 }} />
        </Tooltip>
      );
    case 'LOOM':
      return (
        <Tooltip title="Loom video attached">
          <LoomIcon sx={{ color: '#625DF5', fontSize: 20 }} />
        </Tooltip>
      );
    case 'GOOGLE_DOCS':
      return (
        <Tooltip title="Google Docs attached">
          <DocsIcon sx={{ color: '#4285F4', fontSize: 20 }} />
        </Tooltip>
      );
    case 'GOOGLE_SHEETS':
      return (
        <Tooltip title="Google Sheets attached">
          <SheetsIcon sx={{ color: '#0F9D58', fontSize: 20 }} />
        </Tooltip>
      );
    case 'GOOGLE_SLIDES':
      return (
        <Tooltip title="Google Slides attached">
          <SlidesIcon sx={{ color: '#F4B400', fontSize: 20 }} />
        </Tooltip>
      );
    case 'GOOGLE_DRIVE':
      return (
        <Tooltip title="Google Drive file attached">
          <DriveIcon sx={{ color: '#4285F4', fontSize: 20 }} />
        </Tooltip>
      );
    default:
      return null;
  }
};

// Status badge component
const StatusBadge = ({ isPublished }) => (
  <Chip
    label={isPublished ? 'Published' : 'Draft'}
    size="small"
    sx={{
      backgroundColor: isPublished ? '#E8F5E9' : '#F5F5F5',
      color: isPublished ? '#2E7D32' : '#757575',
      fontWeight: 500,
      fontSize: '0.75rem',
    }}
  />
);

// Empty state component
const EmptyState = ({ onAddClick, hasFilters }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      px: 2,
      textAlign: 'center',
    }}
  >
    <EmptyIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
    <Typography variant="h5" color="text.secondary" gutterBottom>
      {hasFilters ? 'No FAQs match your filters' : 'No FAQs yet'}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400 }}>
      {hasFilters
        ? 'Try adjusting your search or filter criteria to find what you\'re looking for.'
        : 'Get started by creating your first FAQ to help users find answers quickly.'}
    </Typography>
    {!hasFilters && (
      <CustomButton variant="contained" startIcon={<AddIcon />} onClick={onAddClick}>
        Add your first FAQ
      </CustomButton>
    )}
  </Box>
);

// Loading skeleton for table
const TableSkeleton = () => (
  <TableBody>
    {[1, 2, 3, 4, 5].map((row) => (
      <TableRow key={row}>
        <TableCell><Skeleton variant="text" width="80%" /></TableCell>
        <TableCell><Skeleton variant="text" width="60%" /></TableCell>
        <TableCell><Skeleton variant="rounded" width={70} height={24} /></TableCell>
        <TableCell><Skeleton variant="circular" width={20} height={20} /></TableCell>
        <TableCell><Skeleton variant="text" width={100} /></TableCell>
      </TableRow>
    ))}
  </TableBody>
);

// Loading skeleton for cards (mobile)
const CardSkeleton = () => (
  <Stack spacing={2}>
    {[1, 2, 3].map((card) => (
      <Card key={card}>
        <CardContent>
          <Skeleton variant="text" width="70%" height={28} />
          <Skeleton variant="text" width="40%" sx={{ mt: 1 }} />
          <Skeleton variant="text" width="50%" />
        </CardContent>
      </Card>
    ))}
  </Stack>
);

// FAQ Card component for mobile view
const FaqCard = ({ faq, onEdit, onPreview, onDelete }) => (
  <Card 
    sx={{ 
      mb: 2,
      borderLeft: 4,
      borderLeftColor: faq.isPublished ? 'success.main' : 'grey.400',
    }}
  >
    <CardContent sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, pr: 1 }}>
          {faq.question}
        </Typography>
        <MediaProviderIcon provider={faq.videoProvider} />
      </Box>
      
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <StatusBadge isPublished={faq.isPublished} />
        {faq.category && (
          <Chip
            label={faq.category.name}
            size="small"
            variant="outlined"
            icon={<CategoryIcon sx={{ fontSize: '14px !important' }} />}
          />
        )}
      </Stack>
      
      <Typography variant="caption" color="text.secondary">
        Updated: {new Date(faq.updatedAt).toLocaleDateString()}
      </Typography>
    </CardContent>
    
    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
      <IconButton size="small" onClick={() => onPreview(faq)} aria-label="Preview FAQ">
        <PreviewIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => onEdit(faq)} aria-label="Edit FAQ">
        <EditIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => onDelete(faq)} color="error" aria-label="Delete FAQ">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </CardActions>
  </Card>
);

const FaqList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for filters and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState(null);
  
  // State for import/export dialog
  const [importExportOpen, setImportExportOpen] = useState(false);

  // API queries
  const { data: categoriesData, isLoading: categoriesLoading } = useGetFaqCategoriesQuery();
  const { data: faqsData, isLoading: faqsLoading, error: faqsError } = useGetFaqsQuery({
    search: debouncedSearch,
    categoryId: categoryFilter || undefined,
    isPublished: statusFilter !== '' ? statusFilter === 'published' : undefined,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    includeInactive: true,
  });
  
  const [deleteFaq, { isLoading: isDeleting }] = useDeleteFaqMutation();

  // Debounced search handler
  const debouncedSetSearch = useMemo(
    () => debounce((value) => {
      setDebouncedSearch(value);
      setPage(0); // Reset to first page on search
    }, 300),
    []
  );

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSetSearch(value);
  }, [debouncedSetSearch]);

  const handleCategoryChange = (e) => {
    setCategoryFilter(e.target.value);
    setPage(0);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  // Navigation handlers
  const handleAddFaq = () => navigate('/admin/faqs/new');
  const handleEditFaq = (faq) => navigate(`/admin/faqs/${faq.id}/edit`);
  const handlePreviewFaq = (faq) => navigate(`/admin/faqs/${faq.id}/preview`);
  const handleManageCategories = () => navigate('/admin/faq-categories');
  
  // Import/Export handlers
  const handleOpenImportExport = () => setImportExportOpen(true);
  const handleCloseImportExport = () => setImportExportOpen(false);

  // Delete handlers
  const handleDeleteClick = (faq) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (faqToDelete) {
      try {
        await deleteFaq(faqToDelete.id).unwrap();
        setDeleteDialogOpen(false);
        setFaqToDelete(null);
      } catch (error) {
        console.error('Failed to delete FAQ:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setFaqToDelete(null);
  };

  // Pagination handler (custom TablePagination expects onNewPage callback)
  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const categories = categoriesData || [];
  const faqs = faqsData?.data || [];
  const totalCount = faqsData?.total || 0;
  const hasFilters = debouncedSearch || categoryFilter || statusFilter;

  return (
    <MainCard
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">FAQ Management</Typography>
        </Box>
      }
      secondary={
        <Stack direction="row" spacing={1}>
          <CustomButton
            variant="outlined"
            startIcon={<ImportExportIcon />}
            onClick={handleOpenImportExport}
            size="small"
          >
            {isMobile ? 'Import' : 'Import / Export'}
          </CustomButton>
          <CustomButton
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={handleManageCategories}
            size="small"
          >
            {isMobile ? 'Categories' : 'Manage Categories'}
          </CustomButton>
          <CustomButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddFaq}
            size="small"
          >
            {isMobile ? 'Add' : 'Add FAQ'}
          </CustomButton>
        </Stack>
      }
    >
      {/* Filters Section */}
      <Box sx={{ mb: 3 }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          {/* Search */}
          <TextField
            placeholder="Search FAQs..."
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            aria-label="Search FAQs"
          />

          {/* Category Filter */}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={handleCategoryChange}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name} {!cat.isActive && '(Inactive)'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusChange}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {/* Error State */}
      {faqsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load FAQs. Please try again later.
        </Alert>
      )}

      {/* Content */}
      {faqsLoading ? (
        isMobile ? <CardSkeleton /> : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <CustomTableHead>
                <TableRow>
                  <CustomTableHeadCell>Question</CustomTableHeadCell>
                  <CustomTableHeadCell>Category</CustomTableHeadCell>
                  <CustomTableHeadCell>Status</CustomTableHeadCell>
                  <CustomTableHeadCell>Media</CustomTableHeadCell>
                  <CustomTableHeadCell align="right">Actions</CustomTableHeadCell>
                </TableRow>
              </CustomTableHead>
              <TableSkeleton />
            </Table>
          </TableContainer>
        )
      ) : faqs.length === 0 ? (
        <EmptyState onAddClick={handleAddFaq} hasFilters={hasFilters} />
      ) : isMobile ? (
        // Mobile Card View
        <Box>
          {faqs.map((faq) => (
            <FaqCard
              key={faq.id}
              faq={faq}
              onEdit={handleEditFaq}
              onPreview={handlePreviewFaq}
              onDelete={handleDeleteClick}
            />
          ))}
        </Box>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} variant="outlined">
          <Table aria-label="FAQ management table">
            <CustomTableHead>
              <TableRow>
                <CustomTableHeadCell sx={{ width: '40%' }}>Question</CustomTableHeadCell>
                <CustomTableHeadCell>Category</CustomTableHeadCell>
                <CustomTableHeadCell>Status</CustomTableHeadCell>
                <CustomTableHeadCell>Media</CustomTableHeadCell>
                <CustomTableHeadCell align="right">Actions</CustomTableHeadCell>
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {faqs.map((faq) => (
                <CustomTableRow key={faq.id} hover>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      fontWeight={500}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {faq.category ? (
                      <Chip
                        label={faq.category.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          opacity: faq.category.isActive ? 1 : 0.6,
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No category
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge isPublished={faq.isPublished} />
                  </TableCell>
                  <TableCell>
                    <MediaProviderIcon provider={faq.videoProvider} />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0} justifyContent="flex-end">
                      <Tooltip title="Preview">
                        <IconButton 
                          size="small" 
                          onClick={() => handlePreviewFaq(faq)}
                          aria-label="Preview FAQ"
                        >
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditFaq(faq)}
                          aria-label="Edit FAQ"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteClick(faq)}
                          color="error"
                          aria-label="Delete FAQ"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </CustomTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {!faqsLoading && faqs.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <TablePagination
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onNewPage={handlePageChange}
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        handleClose={handleCancelDelete}
        handleConfirm={handleConfirmDelete}
        title="Delete FAQ"
        message={`Are you sure you want to delete this FAQ?\n\n"${faqToDelete?.question}"\n\nThis action cannot be undone.`}
        buttonLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelButtonLabel="Cancel"
      />

      {/* Import/Export Dialog */}
      <FaqImportExport
        open={importExportOpen}
        onClose={handleCloseImportExport}
        onImportSuccess={handleCloseImportExport}
      />
    </MainCard>
  );
};

export default FaqList;
